import React, { useState } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronDown, TrendingUp, TrendingDown, Minus, Equal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';
import type { LeaveStatementView } from '../../models/attendance';

// --- CVA variants ---

const ledgerVariants = cva('rounded-lg border transition-all duration-200', {
  variants: {
    variant: {
      card: 'mt-3',
      standalone: 'w-full p-4',
    },
  },
  defaultVariants: {
    variant: 'card',
  },
});

const rowVariants = cva(
  'flex items-center justify-between py-2 px-3 text-sm',
  {
    variants: {
      type: {
        credit: 'text-emerald-700',
        debit: 'text-red-700',
        neutral: 'text-muted-foreground',
        total: 'text-foreground font-semibold border-t border-border pt-3 mt-1',
      },
    },
    defaultVariants: {
      type: 'neutral',
    },
  }
);

// --- Types ---

export interface BalanceBreakdownLedgerProps extends VariantProps<typeof ledgerVariants> {
  statement?: LeaveStatementView | null;
  isLoading?: boolean;
  className?: string;
  /** Controlled expanded state. If omitted, component manages own state. */
  expanded?: boolean;
  /** Callback when expand toggle is clicked */
  onToggle?: () => void;
}

// --- Skeleton ---

function LedgerSkeleton() {
  return (
    <div className="space-y-2 p-3" data-testid="balance-breakdown-skeleton">
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} className="flex items-center justify-between">
          <div className="h-4 w-32 bg-muted animate-pulse rounded" />
          <div className="h-4 w-16 bg-muted animate-pulse rounded" />
        </div>
      ))}
    </div>
  );
}

// --- Row icons ---

function RowIcon({ type }: { type: 'credit' | 'debit' | 'neutral' | 'total' }) {
  const size = 14;
  switch (type) {
    case 'credit':
      return <TrendingUp size={size} className="shrink-0" />;
    case 'debit':
      return <TrendingDown size={size} className="shrink-0" />;
    case 'total':
      return <Equal size={size} className="shrink-0" />;
    default:
      return <Minus size={size} className="shrink-0 opacity-50" />;
  }
}

// --- Main component ---

export function BalanceBreakdownLedger({
  statement,
  isLoading = false,
  variant = 'card',
  className,
  expanded: controlledExpanded,
  onToggle,
}: BalanceBreakdownLedgerProps) {
  const { t } = useTranslation(['leaves', 'common']);
  const [internalExpanded, setInternalExpanded] = useState(false);
  const resolvedVariant = variant ?? 'card';

  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  const rows = statement
    ? [
        { label: t('opening'), value: statement.openingBalance, type: 'neutral' as const, sign: '' },
        { label: t('accruals'), value: statement.accruals, type: 'credit' as const, sign: '+' },
        { label: t('carryForward'), value: statement.carryForward, type: 'credit' as const, sign: '+' },
        { label: t('adjustments'), value: statement.adjustments, type: statement.adjustments >= 0 ? 'credit' as const : 'debit' as const, sign: statement.adjustments >= 0 ? '+' : '' },
        { label: t('consumed'), value: statement.consumed, type: 'debit' as const, sign: '-' },
        { label: t('forfeited'), value: statement.forfeited, type: 'debit' as const, sign: '-' },
        { label: t('encashed'), value: statement.encashed, type: 'debit' as const, sign: '-' },
      ]
    : [];

  return (
    <div
      className={cn(ledgerVariants({ variant: resolvedVariant }), 'border-border', className)}
      data-testid="balance-breakdown-ledger"
    >
      {/* Trigger */}
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={isExpanded}
        className={cn(
          'flex w-full items-center justify-between px-3 py-2 text-sm font-medium text-muted-foreground',
          'hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
        )}
        data-testid="balance-breakdown-trigger"
      >
        <span>{t('viewBreakdown')}</span>
        <ChevronDown
          size={16}
          className={cn(
            'shrink-0 transition-transform duration-200',
            isExpanded && 'rotate-180'
          )}
        />
      </button>

      {/* Ledger content */}
      {isExpanded && (
        <div
          role="table"
          aria-label={t('balanceBreakdownLabel')}
          className="px-1 pb-3"
        >
          {isLoading ? (
            <LedgerSkeleton />
          ) : !statement ? (
            <div className="p-3 text-sm text-muted-foreground text-center">
              {t('noBreakdownData')}
            </div>
          ) : (
            <>
              {/* Header */}
              <div role="row" className="flex items-center justify-between px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <span role="columnheader">{t('component')}</span>
                <span role="columnheader">{t('days')}</span>
              </div>

              {/* Rows */}
              {rows.map((row) => (
                <div
                  key={row.label}
                  role="row"
                  className={rowVariants({ type: row.type })}
                >
                  <span role="cell" className="flex items-center gap-2">
                    <RowIcon type={row.type} />
                    {row.label}
                  </span>
                  <span role="cell" className="font-mono tabular-nums">
                    {row.sign}{Math.round(Math.abs(row.value) * 100) / 100}
                  </span>
                </div>
              ))}

              {/* Total row */}
              <div
                role="row"
                className={rowVariants({ type: 'total' })}
              >
                <span role="cell" className="flex items-center gap-2">
                  <RowIcon type="total" />
                  {t('closing')}
                </span>
                <span role="cell" className="font-mono tabular-nums">
                  {Math.round(statement.closingBalance * 100) / 100}
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default BalanceBreakdownLedger;
